import { useEffect, useRef } from 'react';
import { useRegisterUndoRedo } from '../../../context/UndoRedoContext';
import { setRecordAction } from '../../../utils/undoRecordBridge';

/**
 * Wire SprintDetail undo/redo into Sidebar context, the undo record bridge,
 * and window-level delete-all requests from the flowchart.
 */
export default function useSprintDetailIntegrations({
  canUndo,
  canRedo,
  undoStack,
  redoStack,
  undo,
  redo,
  recordAction,
  state,
  viewSwitcherProps,
  onOpenDeleteAllModal,
}) {
  const { registerUndoRedo, unregisterUndoRedo } = useRegisterUndoRedo();

  // Refs avoid infinite re-renders (state / viewSwitcherProps change often)
  const stateRef = useRef(state);
  stateRef.current = state;

  const viewSwitcherPropsRef = useRef(viewSwitcherProps);
  viewSwitcherPropsRef.current = viewSwitcherProps;

  useEffect(() => {
    registerUndoRedo({
      canUndo,
      canRedo,
      undoCount: undoStack.length,
      redoCount: redoStack.length,
      onUndo: undo,
      onRedo: redo,
      onToggleHistory: () => stateRef.current.setIsHistoryOpen((prev) => !prev),
      onOpenMissionSearch: viewSwitcherPropsRef.current?.onOpenMissionSearch || null,
    });

    return () => {
      unregisterUndoRedo();
    };
  }, [
    canUndo,
    canRedo,
    undoStack.length,
    redoStack.length,
    undo,
    redo,
    registerUndoRedo,
    unregisterUndoRedo,
  ]);

  useEffect(() => {
    setRecordAction(recordAction);
    return () => setRecordAction(null);
  }, [recordAction]);

  // Flow: Ctrl+A + Delete all missions → confirmation modal
  useEffect(() => {
    const handleRequestDeleteAll = (event) => {
      const detail = event.detail;
      if (detail) onOpenDeleteAllModal(detail);
    };
    window.addEventListener('fokus:requestDeleteAllTasks', handleRequestDeleteAll);
    return () => window.removeEventListener('fokus:requestDeleteAllTasks', handleRequestDeleteAll);
  }, [onOpenDeleteAllModal]);
}
